import { ColorTransform } from "./geom";

/**
 * fl.motion.Color, ported 1:1.
 *
 * The AS3 original also carries `fromXML`/`parseXML`; those depend on the AS3
 * `XML` type and are unreachable in this game, so they are not carried over.
 */
export class Color extends ColorTransform {
  private _tintColor = 0;
  private _tintMultiplier = 0;

  constructor(
    redMultiplier = 1,
    greenMultiplier = 1,
    blueMultiplier = 1,
    alphaMultiplier = 1,
    redOffset = 0,
    greenOffset = 0,
    blueOffset = 0,
    alphaOffset = 0,
  ) {
    super(
      redMultiplier,
      greenMultiplier,
      blueMultiplier,
      alphaMultiplier,
      redOffset,
      greenOffset,
      blueOffset,
      alphaOffset,
    );
  }

  static interpolateTransform(
    from: ColorTransform,
    to: ColorTransform,
    progress: number,
  ): ColorTransform {
    const inverse = 1 - progress;
    return new ColorTransform(
      from.redMultiplier * inverse + to.redMultiplier * progress,
      from.greenMultiplier * inverse + to.greenMultiplier * progress,
      from.blueMultiplier * inverse + to.blueMultiplier * progress,
      from.alphaMultiplier * inverse + to.alphaMultiplier * progress,
      from.redOffset * inverse + to.redOffset * progress,
      from.greenOffset * inverse + to.greenOffset * progress,
      from.blueOffset * inverse + to.blueOffset * progress,
      from.alphaOffset * inverse + to.alphaOffset * progress,
    );
  }

  static interpolateColor(
    fromColor: number,
    toColor: number,
    progress: number,
  ): number {
    const inverse = 1 - progress;
    const fa = (fromColor >> 24) & 0xff;
    const fr = (fromColor >> 16) & 0xff;
    const fg = (fromColor >> 8) & 0xff;
    const fb = fromColor & 0xff;
    const ta = (toColor >> 24) & 0xff;
    const tr = (toColor >> 16) & 0xff;
    const tg = (toColor >> 8) & 0xff;
    const tb = toColor & 0xff;
    const a = (fa * inverse + ta * progress) >>> 0;
    const r = (fr * inverse + tr * progress) >>> 0;
    const g = (fg * inverse + tg * progress) >>> 0;
    const b = (fb * inverse + tb * progress) >>> 0;
    return ((a << 24) | (r << 16) | (g << 8) | b) >>> 0;
  }

  get brightness(): number {
    return this.redOffset ? 1 - this.redMultiplier : this.redMultiplier - 1;
  }

  set brightness(value: number) {
    if (value > 1) value = 1;
    else if (value < -1) value = -1;
    const multiplier = 1 - Math.abs(value);
    const offset = value > 0 ? value * 255 : 0;
    this.redMultiplier =
      this.greenMultiplier =
      this.blueMultiplier =
        multiplier;
    this.redOffset = this.greenOffset = this.blueOffset = offset;
  }

  setTint(tintColor: number, tintMultiplier: number): void {
    this._tintColor = tintColor;
    this._tintMultiplier = tintMultiplier;
    this.redMultiplier =
      this.greenMultiplier =
      this.blueMultiplier =
        1 - tintMultiplier;
    this.redOffset = Math.round(((tintColor >> 16) & 0xff) * tintMultiplier);
    this.greenOffset = Math.round(((tintColor >> 8) & 0xff) * tintMultiplier);
    this.blueOffset = Math.round((tintColor & 0xff) * tintMultiplier);
  }

  get tintColor(): number {
    return this._tintColor;
  }

  set tintColor(value: number) {
    this.setTint(value, this.tintMultiplier);
  }

  get tintMultiplier(): number {
    return this._tintMultiplier;
  }

  set tintMultiplier(value: number) {
    this.setTint(this.tintColor, value);
  }
}
